import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { emergencyKeywords, intents, profileAddress } from '../data/demandes'
import type { AtnaTurn, AtnaTurnKind, ConversationTurn, Intent } from '../types/demandes'

function detect(text: string): Intent {
  const lower = text.toLowerCase()
  return intents.find((intent) => intent.keywords.some((keyword) => lower.includes(keyword))) ?? intents[0]
}

function isEmergency(text: string): boolean {
  const lower = text.toLowerCase()
  return emergencyKeywords.some((keyword) => lower.includes(keyword))
}

const THINKING_MS = 550

/**
 * Drives the ATNA request conversation: the citizen's messages, ATNA's replies
 * and the small state machine (intent -> location -> photo -> detail -> review) behind them.
 * Seeded with the description typed in the launcher, so the thread already has content
 * the moment the dialog opens.
 */
export function useAtnaConversation(initialText: string) {
  const seed = initialText.trim()

  const [turns, setTurns] = useState<ConversationTurn[]>(() => [{ id: 't0', role: 'user', text: seed }])
  const [thinking, setThinking] = useState(true)
  const [draft, setDraft] = useState('')
  const [description, setDescription] = useState(seed)
  const [intent, setIntent] = useState<Intent>(() => detect(seed))
  const [address, setAddress] = useState(profileAddress)
  const [hasPhoto, setHasPhoto] = useState(false)
  const [answer, setAnswer] = useState<string | null>(null)

  const idRef = useRef(1)
  const timerRef = useRef<number | null>(null)

  const reply = useCallback((kind: AtnaTurnKind) => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
    setThinking(true)
    timerRef.current = window.setTimeout(() => {
      idRef.current += 1
      setTurns((current) => [...current, { id: `t${idRef.current}`, role: 'atna', kind }])
      setThinking(false)
    }, THINKING_MS)
  }, [])

  /** Appends the citizen's message, then ATNA's reply after a short beat. */
  const exchange = useCallback(
    (userText: string, kind: AtnaTurnKind, withPhoto = false) => {
      idRef.current += 1
      const id = `t${idRef.current}`
      setTurns((current) => [...current, { id, role: 'user', text: userText, withPhoto }])
      reply(kind)
    },
    [reply],
  )

  useEffect(() => {
    reply(isEmergency(seed) ? 'urgence' : 'comprehension')
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
    // Seeded once when the conversation opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const lastAtna = useMemo(
    () => [...turns].reverse().find((turn): turn is AtnaTurn => turn.role === 'atna'),
    [turns],
  )

  const restart = useCallback(
    (text: string) => {
      setDescription(text)
      setIntent(detect(text))
      setAnswer(null)
      setAddress(profileAddress)
      exchange(text, isEmergency(text) ? 'urgence' : 'comprehension')
    },
    [exchange],
  )

  /** Free-text answers advance whatever question ATNA asked last. */
  const submitDraft = useCallback(() => {
    const text = draft.trim()
    if (!text || thinking) return
    setDraft('')

    switch (lastAtna?.kind) {
      case 'lieu':
        setAddress(text)
        exchange(text, hasPhoto ? 'detail' : 'photo')
        break
      case 'photo':
        exchange(text, 'detail')
        break
      case 'detail':
        setAnswer(text)
        exchange(text, 'revue')
        break
      case 'urgence':
      case 'comprehension':
      default:
        restart(text)
    }
  }, [draft, exchange, hasPhoto, lastAtna, restart, thinking])

  const attachPhoto = useCallback(() => {
    setHasPhoto(true)
    if (lastAtna?.kind === 'photo') exchange('I added a photo', 'detail', true)
  }, [exchange, lastAtna])

  return {
    turns,
    thinking,
    draft,
    setDraft,
    submitDraft,
    description,
    intent,
    setIntent,
    address,
    setAddress,
    answer,
    setAnswer,
    hasPhoto,
    attachPhoto,
    exchange,
    detect,
    liveAtnaId: lastAtna?.id,
    lastAtnaKind: lastAtna?.kind,
  }
}
